import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import { Loader } from '../Loader';
import { SubscriptionPlanSelector } from '../SubscriptionPlanSelector';
import { RiskConcernSummary } from '../RiskConcernSummary';
import { Checkout } from '../Checkout';
import { Icon } from '../Icon';
import { useSubscriptionPlans } from '../../shared/use-subscription-plans';
import { useIntersectionTracking } from '../../shared/use-intersection-tracking';
import { scrollToPlans } from '../../shared/scroll-helpers';
import { createSubscriptionForm } from '../../../lib/use-cases/create-subscription';
import { handleCreateSubscriptionError } from '../../shared/error-helpers';
import { resolveFirstStepUrl } from '../../../lib/shared/upsell-flow';
import { getCurrentPrelander } from '../../../lib/shared/prelander-helpers';
import { getMetadata } from '../../../lib/shared/ad-helpers';
import { logEvent } from '../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../lib/shared/event-types';
import { FP_PROMO_MAIN_TYPE } from '../../../lib/shared/promotion-type';
import { getChapterLabel, getChapterCategory } from '../../shared/chapter-answer-helpers';
import { getPlanDiscountPercent } from '../../shared/plan-helpers';
import {
    MK_FOUR_WEEKS_2999_FOUR_WEEKS_1999_INTRO_PLAN,
    MK_FOUR_WEEKS_2999_WITHOUT_TRIAL_PLAN,
    MK_FOUR_WEEKS_2999_ONE_WEEK_899_TRIAL_PLAN,
    MK_TWELVE_WEEKS_5999_TWELVE_WEEKS_3499_INTRO_PLAN,
    MK_TWELVE_WEEKS_5999_WITHOUT_TRIAL_PLAN
} from '../../../lib/shared/subscription-plans';
import beforeComparisonImageSrc from '../../assets/images/sale/memory-sale-before-dad.png';
import afterComparisonImageSrc from '../../assets/images/sale/memory-sale-after-dad.png';
import ebookImageSrc from '../../assets/images/sale/memory-sale-ebook-dad.png';
import styles from './SaleV2.css';

const cx = classnames.bind(styles);

const PEN_COMPARISON_ROWS = [
    'A finished chapter in his first week',
    'Questions that follow what he said',
    'Nothing wasted if he stops',
    'Kept forever, never deleted'
];

const plansWithoutPromo = [MK_FOUR_WEEKS_2999_ONE_WEEK_899_TRIAL_PLAN, MK_FOUR_WEEKS_2999_WITHOUT_TRIAL_PLAN, MK_TWELVE_WEEKS_5999_WITHOUT_TRIAL_PLAN];
const plansWithMainPromo = [MK_FOUR_WEEKS_2999_ONE_WEEK_899_TRIAL_PLAN, MK_FOUR_WEEKS_2999_FOUR_WEEKS_1999_INTRO_PLAN, MK_TWELVE_WEEKS_5999_TWELVE_WEEKS_3499_INTRO_PLAN];

function SaleV2({
    data,
    initialPrelander,
    expiryTimestamp,
    onExpirePromo = () => {},
    isExpired = true,
    selectSubscription,
    selectedSubscriptionData,
    merchantData,
    setMerchantData,
    legalLinks
}) {
    const { plans, isLoading } = useSubscriptionPlans([], isExpired);
    const [isPending, setIsPending] = useState(false);
    const [activePlanData, setActivePlanData] = useState({});
    const firstUpsellUrl = resolveFirstStepUrl(getCurrentPrelander(), merchantData.subscriptionId);
    const chapterLabel = getChapterLabel(data.userResponses);

    useIntersectionTracking('.fp-block');

    const handleClose = () => {
        selectSubscription({});
        setMerchantData({});
        scrollToPlans();
    };

    useEffect(() => {
        const handleNavigation = e => {
            if (e.navigationType === 'traverse' && window.history.state?.checkoutVisible) {
                window.history.replaceState({ checkoutVisible: false }, '');
                handleClose();
            }
        };

        logEvent(EVENT_TYPES.SALE_PAGE_VISITED, { chapter: chapterLabel });

        window.navigation.addEventListener('navigate', handleNavigation);
        document.getElementById('root').scrollIntoView({ block: 'start', behavior: 'smooth' });

        return () => {
            window.navigation.removeEventListener('navigate', handleNavigation);
        };
    }, []);

    const getPlans = () => {
        if (!isExpired && data.promoType === FP_PROMO_MAIN_TYPE) {
            return plansWithMainPromo.map(plan => plans.find(item => item.plan === plan)).filter(Boolean);
        }

        return plansWithoutPromo.map(plan => plans.find(item => item.plan === plan)).filter(Boolean);
    };

    useEffect(() => {
        const newPlans = getPlans();

        if (newPlans[0]) {
            setActivePlanData(newPlans[0]);
        }
    }, [plans, isExpired]);

    const submitSubscription = async subscriptionData => {
        try {
            setIsPending(true);

            const newMerchantData = await createSubscriptionForm({
                plan: subscriptionData.plan,
                email: data.email,
                metadata: {
                    ...getMetadata(window.location.search, initialPrelander, data.userResponses),
                    memoryInitialChapter: getChapterCategory(data.userResponses)
                }
            });

            window.history.pushState({ checkoutVisible: true }, '');
            setMerchantData(newMerchantData);
            logEvent(EVENT_TYPES.SALE_CHECKOUT_INITIATE);
        } catch (e) {
            handleCreateSubscriptionError(e);
        } finally {
            setIsPending(false);
        }
    };

    const tryAgain = async (subscriptionData, subscriptionId) => {
        try {
            setIsPending(true);

            const newMerchantData = await createSubscriptionForm({
                plan: subscriptionData.plan,
                email: data.email,
                id: subscriptionId,
                metadata: {
                    ...getMetadata(window.location.search, initialPrelander, data.userResponses),
                    memoryInitialChapter: getChapterCategory(data.userResponses)
                }
            });

            setMerchantData(newMerchantData);
        } catch (e) {
            handleCreateSubscriptionError(e);
        } finally {
            setIsPending(false);
        }
    };

    const handleSubscribe = subscriptionData => {
        logEvent(EVENT_TYPES.PLAN_SELECTED, { plan: subscriptionData.plan });
        selectSubscription(subscriptionData);
        submitSubscription(subscriptionData);
    };

    if (merchantData.merchant) {
        return (
            <div className={styles.fixedModal}>
                <Checkout
                    merchantData={merchantData}
                    subscriptionData={selectedSubscriptionData}
                    promoType={isExpired ? '' : data.promoType}
                    onClose={handleClose}
                    tryAgain={tryAgain}
                    isPending={isPending}
                    successUrl={firstUpsellUrl}
                    termsOfUseLink={legalLinks.termsOfUse}
                    defaultDiscountValue={getPlanDiscountPercent(selectedSubscriptionData)}
                    isWalletEnabled
                />
            </div>
        );
    }

    const renderPlans = (hasPaymentInformation = true) => {
        const newPlans = getPlans();

        return (
            <>
                {isLoading
                    ? <Loader className={styles.loader} variant='secondary' />
                    : (
                        <SubscriptionPlanSelector
                            onSubmit={handleSubscribe}
                            selectedSubscriptionData={activePlanData}
                            selectSubscription={setActivePlanData}
                            isPending={isPending}
                            plans={newPlans}
                            expiryTimestamp={expiryTimestamp}
                            onExpirePromo={onExpirePromo}
                            isExpired={isExpired}
                            className={styles.blockWithIndent}
                            hasPaymentInformation={hasPaymentInformation}
                        />
                    )
                }
            </>
        );
    };

    return (
        <div className={styles.root}>
            <section className={styles.comparison}>
                <div className={styles.comparisonTop}>
                    <header className={styles.comparisonHeader}>
                        <span>Right now</span>
                        <Icon name='chevronRightDoubleDark' />
                        <span>with Boundly</span>
                    </header>
                    <div className={styles.comparisonImages}>
                        <img
                            src={`${beforeComparisonImageSrc}?v=2`}
                            alt='Before Boundly'
                        />
                        <img
                            src={`${afterComparisonImageSrc}?v=2`}
                            alt='With Boundly'
                        />
                    </div>
                </div>
                <div className={styles.comparisonMetrics}>
                    <div className={styles.metricColumn}>
                        <div className={styles.metricBlock}>
                            <p className={styles.metricName}>His story</p>
                            <p className={styles.metricValue}>Fading into silence</p>
                        </div>
                        <div className={styles.metricRowDivider} />
                        <div className={styles.metricBlock}>
                            <p className={styles.metricName}>Future regret</p>
                            <div className={styles.progressBar}>
                                <div className={cx('progressFill', 'danger', 'fillLong')} />
                            </div>
                        </div>
                        <div className={styles.metricRowDivider} />
                        <div className={styles.metricBlock}>
                            <p className={styles.metricName}>His secrets</p>
                            <p className={styles.metricValue}>Lost forever</p>
                            <div className={styles.segmentedBar}>
                                <div className={cx('segment', 'danger')} />
                                <div className={cx('segment', 'empty')} />
                                <div className={cx('segment', 'empty')} />
                            </div>
                        </div>
                        <div className={styles.metricRowDivider} />
                        <div className={styles.metricBlock}>
                            <p className={styles.metricName}>Your feelings</p>
                            <p className={styles.metricValue}>Am I too late?</p>
                        </div>
                    </div>
                    <div className={styles.verticalDivider} />
                    <div className={styles.metricColumn}>
                        <div className={styles.metricBlock}>
                            <p className={styles.metricName}>His story</p>
                            <p className={styles.metricValue}>Saved in his voice</p>
                        </div>
                        <div className={styles.metricRowDivider} />
                        <div className={styles.metricBlock}>
                            <p className={styles.metricName}>Future regret</p>
                            <div className={styles.progressBar}>
                                <div className={cx('progressFill', 'success', 'fillShort')} />
                            </div>
                        </div>
                        <div className={styles.metricRowDivider} />
                        <div className={styles.metricBlock}>
                            <p className={styles.metricName}>His secrets</p>
                            <p className={styles.metricValue}>Passed to children</p>
                            <div className={styles.segmentedBar}>
                                <div className={cx('segment', 'success')} />
                                <div className={cx('segment', 'success')} />
                                <div className={cx('segment', 'success')} />
                            </div>
                        </div>
                        <div className={styles.metricRowDivider} />
                        <div className={styles.metricBlock}>
                            <p className={styles.metricName}>Your feelings</p>
                            <p className={styles.metricValue}>Total peace of mind</p>
                        </div>
                    </div>
                </div>
            </section>
            <div id='subscription-start-block' className={styles.subscriptionStartBlock} />
            <div className={styles.storyIntro}>
                <h2 className={styles.storyTitle}>
                    Start his <em>truly priceless life story.</em> It&apos;s worth every bit of it.
                </h2>
                <RiskConcernSummary chapterLabel={chapterLabel} />
            </div>
            <div id={EVENT_TYPES.FIRST_PLAN_VIEWED} className='fp-block' />
            {renderPlans()}
            <div className={styles.ebookBlock}>
                <div className={styles.ebookImageWrapper}>
                    <img src={ebookImageSrc} alt='His story as an Ebook' />
                </div>
                <div className={styles.ebookCard}>
                    <h3 className={styles.ebookTitle}>His story as an Ebook</h3>
                    <p className={styles.ebookText}>
                        When the last chapter is done, everything he wrote becomes <strong>one book — edited, laid out, with his photographs in it.</strong>
                    </p>
                    <p className={styles.ebookText}>His to keep, yours to read, theirs to inherit.</p>
                </div>
            </div>
            <div className={styles.penComparison}>
                <div className={styles.penComparisonHeader}>
                    <h2 className={styles.penComparisonTitle}>
                        Not a book he has to fill in <em>with a pen.</em>
                    </h2>
                    <p className={styles.penComparisonSubtitle}>And not one that&apos;s worthless until it&apos;s finished.</p>
                </div>
                <div className={styles.table}>
                    <div className={styles.tableHead}>
                        <span className={styles.tableHeadSpacer} />
                        <span className={styles.tableHeadCell}>Paper<br />books</span>
                        <span className={styles.tableHeadCell}>Boundly</span>
                    </div>
                    <ul className={styles.tableBody}>
                        {PEN_COMPARISON_ROWS.map(row => (
                            <li className={styles.tableRow} key={row}>
                                <p className={styles.tableRowText}>{row}</p>
                                <span className={styles.tableCell}>
                                    <Icon name='close' />
                                </span>
                                <span className={cx('tableCell', 'success')}>
                                    <Icon name='check' />
                                </span>
                            </li>
                        ))}
                    </ul>
                    <div className={styles.tableFoot}>
                        <p className={styles.tableFootLabel}>What you get</p>
                        <div className={styles.tableOutcome}>
                            <p>A book</p>
                            <p>in a year</p>
                        </div>
                        <div className={cx('tableOutcome', 'success')}>
                            <p>A chapter</p>
                            <p>this week</p>
                        </div>
                    </div>
                </div>
            </div>
            {renderPlans(false)}
        </div>
    );
}

SaleV2.propTypes = {
    data: PropTypes.object.isRequired,
    expiryTimestamp: PropTypes.any,
    onExpirePromo: PropTypes.func,
    isExpired: PropTypes.bool,
    initialPrelander: PropTypes.string.isRequired,
    legalLinks: PropTypes.object.isRequired
};

export {
    SaleV2
};
